import React, { useEffect, useState } from 'react';
import crmClient from './crmClient';
import { formatNumber } from './format';

const scoreTone = score => {
  if (score >= 75) return 'green';
  if (score >= 50) return 'teal';
  if (score >= 30) return 'amber';
  return 'slate';
};

const AIProspectingView = ({ meta }) => {
  const [providerConfigured, setProviderConfigured] = useState(true);
  const [form, setForm] = useState({
    query_text: '',
    location: '',
    radius: '',
    industry: '',
    count: 25,
    min_score: 0,
    depth: 'standard',
    include_existing: false
  });
  const [running, setRunning] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [lastSearch, setLastSearch] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selected, setSelected] = useState({});
  const [detail, setDetail] = useState(null);
  const [minScoreFilter, setMinScoreFilter] = useState(0);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    crmClient
      .prospectingMeta()
      .then(payload => setProviderConfigured(!payload || payload.provider_configured !== false))
      .catch(() => {});
  }, []);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const runSearch = () => {
    setRunning(true);
    setError(null);
    setNotice(null);
    setSelected({});
    crmClient
      .runProspecting(form)
      .then(payload => {
        setRunning(false);
        setProspects((payload && payload.prospects) || []);
        setLastSearch((payload && payload.search) || null);
      })
      .catch(err => {
        setRunning(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Search failed.');
      });
  };

  const visible = prospects.filter(p => (p.gophr_fit_score || 0) >= minScoreFilter);
  const selectedIds = Object.keys(selected).filter(id => selected[id]);

  const toggle = id => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const selectAllQualified = () => {
    const next = {};
    visible.forEach(p => {
      if (p.status !== 'added' && p.dedupe_status !== 'duplicate') next[p.crm_prospect_id] = true;
    });
    setSelected(next);
  };

  const addSelected = () => {
    if (!selectedIds.length) return;
    setAdding(true);
    setError(null);
    crmClient
      .addProspects(selectedIds)
      .then(payload => {
        setAdding(false);
        const s = (payload && payload.summary) || {};
        setNotice(`Added ${s.added || 0} to the CRM${s.skipped ? `, ${s.skipped} skipped (already present)` : ''}.`);
        setProspects(prev =>
          prev.map(p => (selectedIds.includes(p.crm_prospect_id) ? { ...p, status: 'added' } : p))
        );
        setSelected({});
      })
      .catch(err => {
        setAdding(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to add prospects.');
      });
  };

  return (
    <div className="crm-prospecting">
      <div className="crm-card">
        <h3>AI Prospecting</h3>
        <p className="crm-muted">
          Describe who you want to find. The AI searches the web for real companies, scores each for Gophr fit, checks them against
          the CRM, and lets you review before adding.
        </p>
        {!providerConfigured && (
          <div className="crm-inline-error">
            The AI provider isn’t configured on the server yet (OPENAI_API_KEY). Searches won’t run until it’s added.
          </div>
        )}
        <label className="crm-field-grow">
          <span>What are you looking for?</span>
          <textarea
            rows={2}
            value={form.query_text}
            placeholder="e.g. Find 50 industrial and oilfield companies within 75 miles of Lake Charles, Louisiana"
            onChange={event => update('query_text', event.target.value)}
          />
        </label>
        <div className="crm-field-row">
          <label className="crm-field-grow">
            <span>Location</span>
            <input value={form.location} placeholder="Lake Charles, LA" onChange={event => update('location', event.target.value)} />
          </label>
          <label>
            <span>Radius (mi)</span>
            <input type="number" value={form.radius} onChange={event => update('radius', event.target.value)} style={{ width: 90 }} />
          </label>
          <label className="crm-field-grow">
            <span>Industry</span>
            <input value={form.industry} placeholder="oilfield, valves, MRO…" onChange={event => update('industry', event.target.value)} />
          </label>
        </div>
        <div className="crm-field-row">
          <label>
            <span># Prospects</span>
            <input type="number" value={form.count} min={1} max={100} onChange={event => update('count', event.target.value)} style={{ width: 90 }} />
          </label>
          <label>
            <span>Min fit score</span>
            <input type="number" value={form.min_score} min={0} max={100} onChange={event => update('min_score', event.target.value)} style={{ width: 90 }} />
          </label>
          <label>
            <span>Depth</span>
            <select value={form.depth} onChange={event => update('depth', event.target.value)}>
              <option value="quick">Quick</option>
              <option value="standard">Standard</option>
              <option value="deep">Deep research</option>
            </select>
          </label>
          <label className="crm-date-filter">
            <input type="checkbox" checked={form.include_existing} onChange={event => update('include_existing', event.target.checked)} />
            <span>Include existing CRM companies</span>
          </label>
        </div>
        <div className="crm-detail-actions">
          <button type="button" className="crm-btn" disabled={running || (!form.query_text && !form.location && !form.industry)} onClick={runSearch}>
            {running ? 'Researching…' : 'Find prospects'}
          </button>
          {lastSearch && (
            <span className="crm-muted">
              Found {formatNumber(lastSearch.companies_found)} · {formatNumber(lastSearch.qualified_count)} qualified ·{' '}
              {formatNumber(lastSearch.duplicate_count)} already in CRM · est. cost ${Number(lastSearch.estimated_cost || 0).toFixed(2)}
            </span>
          )}
        </div>
        {error && <div className="crm-inline-error">{error}</div>}
        {notice && <div className="crm-preview-banner">{notice}</div>}
        {running && <p className="crm-muted">Searching the web and scoring companies — this can take up to a minute…</p>}
      </div>

      {prospects.length > 0 && (
        <>
          <div className="crm-filters">
            <label className="crm-date-filter">
              <span>Min score</span>
              <input type="number" value={minScoreFilter} min={0} max={100} onChange={event => setMinScoreFilter(Number(event.target.value) || 0)} style={{ width: 80 }} />
            </label>
            <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm" onClick={selectAllQualified}>
              Select all new
            </button>
            <button type="button" className="crm-btn crm-btn--sm" disabled={adding || !selectedIds.length} onClick={addSelected}>
              {adding ? 'Adding…' : `Add ${selectedIds.length} to CRM`}
            </button>
          </div>

          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr>
                  <th />
                  <th>Score</th>
                  <th>Company</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Industry</th>
                  <th>Service</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(p => (
                  <tr key={p.crm_prospect_id} className={detail && detail.crm_prospect_id === p.crm_prospect_id ? 'crm-row-active' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(selected[p.crm_prospect_id])}
                        disabled={p.status === 'added'}
                        onChange={() => toggle(p.crm_prospect_id)}
                      />
                    </td>
                    <td>
                      <span className={`crm-stage-pill crm-stage-pill--${scoreTone(p.gophr_fit_score)}`}>{p.gophr_fit_score}</span>
                    </td>
                    <td onClick={() => setDetail(p)} style={{ cursor: 'pointer' }}>
                      <strong>{p.business_name}</strong>
                      {p.parent_name && <small className="crm-muted"> · {p.parent_name}</small>}
                    </td>
                    <td>{p.city || '—'}</td>
                    <td>{p.state || '—'}</td>
                    <td>{p.industry || '—'}</td>
                    <td>{p.recommended_service || '—'}</td>
                    <td>
                      {p.status === 'added' ? (
                        <span className="crm-stage-pill crm-stage-pill--green">Added</span>
                      ) : p.dedupe_status === 'duplicate' ? (
                        <span className="crm-stage-pill crm-stage-pill--amber">In CRM</span>
                      ) : p.dedupe_status === 'possible' ? (
                        <span className="crm-stage-pill crm-stage-pill--slate">Possible dup</span>
                      ) : (
                        <span className="crm-stage-pill crm-stage-pill--blue">New</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {detail && (
        <div className="crm-modal" role="dialog" aria-modal="true">
          <div className="crm-modal-card crm-prospect-detail">
            <div className="crm-detail-head">
              <div>
                <span className={`crm-stage-pill crm-stage-pill--${scoreTone(detail.gophr_fit_score)}`}>Fit {detail.gophr_fit_score}</span>
                <h3>{detail.business_name}</h3>
                {detail.parent_name && <p className="crm-muted">Parent: {detail.parent_name}</p>}
              </div>
              <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm" onClick={() => setDetail(null)}>
                Close
              </button>
            </div>
            <p>
              {[detail.address, detail.city, detail.state, detail.zip].filter(Boolean).join(', ') || 'Address unknown'}
            </p>
            <div className="crm-field-row">
              {detail.phone && <span>📞 {detail.phone}</span>}
              {detail.website && (
                <span>
                  🌐{' '}
                  <a href={detail.website.startsWith('http') ? detail.website : `https://${detail.website}`} target="_blank" rel="noreferrer">
                    {detail.website}
                  </a>
                </span>
              )}
            </div>
            {detail.description && <p>{detail.description}</p>}

            <h4>Why Gophr should target them</h4>
            <p>{(detail.ai_analysis && detail.ai_analysis.why_gophr) || '—'}</p>

            <h4>Score breakdown</h4>
            <ul className="crm-score-breakdown">
              {Object.entries(detail.score_breakdown || {}).map(([k, v]) => (
                <li key={k}>
                  <span>{k.replace(/_/g, ' ')}</span>
                  <strong>{v}</strong>
                </li>
              ))}
            </ul>

            <div className="crm-field-row">
              <div>
                <h4>Recommended service</h4>
                <p>{detail.recommended_service || '—'}</p>
              </div>
              <div>
                <h4>Likely department</h4>
                <p>{detail.likely_department || '—'}</p>
              </div>
            </div>
            <h4>Recommended titles</h4>
            <p>{Array.isArray(detail.recommended_titles) && detail.recommended_titles.length ? detail.recommended_titles.join(', ') : '—'}</p>
            <h4>Sales angle</h4>
            <p>{(detail.ai_analysis && detail.ai_analysis.sales_angle) || '—'}</p>

            {Array.isArray(detail.source_urls) && detail.source_urls.length > 0 && (
              <>
                <h4>Sources</h4>
                <ul>
                  {detail.source_urls.map((u, i) => (
                    <li key={i}>
                      <a href={u} target="_blank" rel="noreferrer">
                        {u}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="crm-detail-actions">
              {detail.status !== 'added' && detail.dedupe_status !== 'duplicate' && (
                <button
                  type="button"
                  className="crm-btn"
                  disabled={adding}
                  onClick={() => {
                    setSelected(prev => ({ ...prev, [detail.crm_prospect_id]: true }));
                    setDetail(null);
                  }}
                >
                  Select to add
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIProspectingView;
